import { NavLink, useNavigate, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  LayoutDashboard, Users, BookOpen, FileText, Receipt, Settings, LogOut,
  Search, Menu, X, Wrench, Calendar, Briefcase, Clock, ReceiptText, Car,
  UserCog, BarChart3, Mail, RotateCw, History, Home, Inbox, DollarSign,
  Megaphone, GitBranch, Plus, Grid3x3,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/apiClient";
import NotificationsBell from "./NotificationsBell";
import ThemeToggle from "./ThemeToggle";

// Top-level nav (HCP-style — short, opinionated labels matching the screenshot)
const TOP_NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/dashboard-v3", label: "Dash", icon: LayoutDashboard },
  { to: "/emails", label: "Inbox", icon: Inbox },
  { to: "/schedule", label: "Schedule", icon: Calendar },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/my-money", label: "My money", icon: DollarSign },
  { to: "/reports", label: "Reporting", icon: BarChart3 },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/pricebook", label: "Price book", icon: BookOpen },
  { to: "/estimates", label: "Pipeline", icon: GitBranch },
];

// Secondary items — shown under "More" grid menu
const MORE_NAV = [
  { to: "/jobs", label: "Open Jobs", icon: Briefcase },
  { to: "/invoices", label: "Invoices", icon: Receipt },
  { to: "/timesheets", label: "Timesheets", icon: Clock },
  { to: "/expenses", label: "Expenses", icon: ReceiptText },
  { to: "/mileage", label: "Mileage", icon: Car },
  { to: "/recurring", label: "Recurring", icon: RotateCw },
  { to: "/employees", label: "Employees", icon: UserCog },
  { to: "/email-settings", label: "Email Tmpls", icon: Mail, adminOnly: true },
  { to: "/audit", label: "Audit Log", icon: History, adminOnly: true },
];

const NEW_ITEMS = [
  // "Job" opens the unified job/invoice form (Jobs ARE invoices in this app)
  { label: "Job", to: "/jobs/new", icon: Briefcase },
  { label: "Estimate", to: "/estimates/new", icon: FileText },
  { label: "Customer", to: "/customers/new", icon: Users },
];

function GlobalSearch({ compact }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const nav = useNavigate();

  useEffect(() => {
    if (!q || q.length < 1) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data); setOpen(true);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goto = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <div ref={ref} className={`relative ${compact ? "" : "max-w-2xl flex-1"}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Search"
          className="w-full pl-9 pr-3 py-1.5 text-sm bg-white/10 text-white placeholder-slate-300 border border-white/10 rounded-md focus:outline-none focus:bg-white focus:text-slate-900 focus:placeholder-slate-400 focus:border-slate-300 transition-colors"
        />
      </div>
      {open && results && (
        <div data-testid="search-results" className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-96 overflow-auto">
          {["customers", "jobs", "invoices", "estimates"].map((g) => results[g]?.length > 0 && (
            <div key={g}>
              <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">{g}</div>
              {results[g].map((r) => {
                const path = g === "customers" ? `/customers/${r.id}`
                          : g === "jobs" ? `/jobs/${r.id}`
                          : g === "invoices" ? `/jobs/${r.job_id || r.id}`
                          : `/${g}/${r.id}`;
                const primary = r.name || r.company_name || r.number || "—";
                const secondary = g === "jobs"
                  ? [r.customer_name, r.service_address, r.job_type].filter(Boolean).join(" · ")
                  : g === "customers"
                    ? (r.email || r.phone || r.billing_address || "")
                    : (r.customer_name || r.status || "");
                return (
                  <button
                    key={r.id}
                    data-testid={`search-result-${g}-${r.id}`}
                    onClick={() => goto(path)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 flex justify-between items-center gap-3"
                  >
                    <span className="text-sm text-slate-900 flex-1 min-w-0 break-words pr-2">{primary}</span>
                    <span className="text-xs text-slate-500 truncate max-w-[45%] shrink-0">{secondary}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {!results.customers?.length && !results.jobs?.length && !results.invoices?.length && !results.estimates?.length && (
            <div className="p-4 text-sm text-slate-500">No results</div>
          )}
        </div>
      )}
    </div>
  );
}

function NewMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        data-testid="new-menu-btn"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-1.5 bg-white text-slate-900 hover:bg-slate-100 text-sm font-semibold rounded-md flex items-center gap-1"
      >
        <Plus size={14} strokeWidth={3} /> New
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl min-w-[180px] py-1 z-50" data-testid="new-menu-dropdown">
          {NEW_ITEMS.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`new-menu-${n.label.toLowerCase()}`}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <n.icon size={14} className="text-slate-400" /> {n.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MoreMenu({ items, label = "More", testid = "more-menu" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        data-testid={`${testid}-btn`}
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 text-slate-300 hover:text-white hover:bg-white/10 rounded-md"
        title={label}
      >
        <Grid3x3 size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl min-w-[200px] py-1 z-50" data-testid={`${testid}-dropdown`}>
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <n.icon size={14} className="text-slate-400" /> {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const initials = (user?.full_name || user?.email || "?").split(/[\s@]/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div ref={ref} className="relative">
      <button
        data-testid="profile-menu-btn"
        onClick={() => setOpen((v) => !v)}
        className="w-8 h-8 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold flex items-center justify-center"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl min-w-[220px] z-50">
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-xs text-slate-500">Signed in as</div>
            <div className="text-sm font-medium text-slate-900 truncate">{user?.email}</div>
          </div>
          <Link to="/settings" onClick={() => setOpen(false)} data-testid="profile-menu-settings" className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <Settings size={14} className="text-slate-400" /> Settings
          </Link>
          <button onClick={onLogout} data-testid="logout-btn" className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100">
            <LogOut size={14} className="text-slate-400" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const allMobileItems = [...TOP_NAV, ...MORE_NAV.filter((m) => !m.adminOnly || user?.role === "admin"), { to: "/settings", label: "Settings", icon: Settings }];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-slate-900 text-white" data-testid="top-nav">
        <div className="px-4 lg:px-6 h-14 flex items-center gap-1">
          <button
            className="lg:hidden p-2 text-slate-300 hover:text-white"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>

          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 pr-4 mr-1 border-r border-white/10 h-full pl-1">
            <div className="w-7 h-7 rounded bg-[#0F4C81] flex items-center justify-center shrink-0">
              <Wrench className="text-white" size={15} strokeWidth={2.5} />
            </div>
            <div className="hidden md:block">
              <div className="font-display font-bold text-[13px] leading-tight">PlumbOps</div>
              <div className="text-[9px] text-slate-400 tracking-wider">FIELD OPS · CRM</div>
            </div>
          </Link>

          {/* Top nav links (desktop) */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 overflow-x-auto">
            {TOP_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `px-2.5 py-1.5 rounded text-[13px] font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1 lg:hidden" />

          {/* Right cluster */}
          <div className="flex items-center gap-1.5 ml-auto">
            <div className="hidden md:block w-64 lg:w-96 xl:w-[28rem]">
              <GlobalSearch />
            </div>
            <NewMenu />
            <NotificationsBell />
            <span className="hidden md:flex w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)] mx-1" title="Online" data-testid="online-indicator" />
            <MoreMenu items={MORE_NAV.filter((m) => !m.adminOnly || user?.role === "admin")} label="More" testid="more-menu" />
            <Link to="/settings" data-testid="nav-settings-icon" className="p-1.5 text-slate-300 hover:text-white hover:bg-white/10 rounded-md" title="Settings">
              <Settings size={16} />
            </Link>
            <ThemeToggle />
            <ProfileMenu user={user} onLogout={logout} />
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="bg-black/40 flex-1" onClick={() => setMobileOpen(false)} />
          <aside className="w-72 bg-white flex flex-col h-full">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display font-bold text-slate-900">PlumbOps</div>
              <button onClick={() => setMobileOpen(false)} className="p-1 text-slate-500"><X size={18}/></button>
            </div>
            <nav className="flex-1 overflow-y-auto p-2">
              {allMobileItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                      isActive ? "bg-[#0F4C81] text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                >
                  <n.icon size={16} /> {n.label}
                </NavLink>
              ))}
            </nav>
            <button onClick={logout} className="m-2 flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-slate-700 hover:bg-slate-100"><LogOut size={16}/> Sign out</button>
          </aside>
        </div>
      )}

      <main className="flex-1 p-4 lg:p-8 bg-slate-50/30 dark:bg-slate-950 text-slate-900 dark:text-slate-100 max-w-[1600px] w-full mx-auto">
        {children}
      </main>
    </div>
  );
}
